import { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../../../environment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

interface ApiClient {
  api_client_id: number;
  client_name: string;
  client_id: string;
  client_logo: string | null;
}

interface UsageFiltersProps {
  dateRange: [Date | null, Date | null];
  selectedClientId: number | null;
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  onClientChange: (clientId: number | null) => void;
}

const UsageFilters: React.FC<UsageFiltersProps> = ({
  dateRange,
  selectedClientId,
  onDateRangeChange,
  onClientChange,
}) => {
  const [apiClients, setApiClients] = useState<ApiClient[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        new Date(new Date().setDate(new Date().getDate() - 30)),
        new Date(),
      ],
      "Last 7 Days": [
        new Date(new Date().setDate(new Date().getDate() - 7)),
        new Date(),
      ],
      "Last Month": [
        new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
        new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      ],
      "This Month": [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(),
      ],
      Today: [new Date(), new Date()],
      Yesterday: [
        new Date(new Date().setDate(new Date().getDate() - 1)),
        new Date(new Date().setDate(new Date().getDate() - 1)),
      ],
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    timePicker: false,
  };

  useEffect(() => {
    fetchApiClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchApiClients = async (searchQuery: string = "") => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api-client/`;
      if (searchQuery.trim()) {
        url += `?client_name=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiClients(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching API clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSearch = (query: string) => {
    setClientSearchQuery(query);
    fetchApiClients(query);
  };

  const handleClientSelect = (client: ApiClient) => {
    onClientChange(client.api_client_id);
    setShowClientDropdown(false);
    setClientSearchQuery("");
  };

  const handleClearClient = () => {
    onClientChange(null);
    setClientSearchQuery("");
  };

  const getSelectedClientName = () => {
    const client = apiClients.find((c) => c.api_client_id === selectedClientId);
    return client?.client_name || "All Clients";
  };

  const handleClearFilters = () => {
    onDateRangeChange(null, null);
    onClientChange(null);
    setClientSearchQuery("");
  };

  return (
    <>
      <style>
        {`
          .usage-filters-container {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
          }

          .filter-group {
            display: flex;
            gap: 15px;
            align-items: flex-end;
            flex-wrap: wrap;
          }

          .filter-item {
            flex: 1;
            min-width: 200px;
          }

          .filter-item label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 6px;
          }

          .icon-form {
            position: relative;
          }

          .icon-form .form-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1;
            color: #6c757d;
          }

          .icon-form input {
            padding-left: 34px !important;
          }

          .icon-form.filter-active input {
            border-color: #824EEB;
            box-shadow: 0 0 0 0.2rem rgba(130, 78, 235, 0.25);
          }

          .filter-item input[type="text"] {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            font-size: 14px;
            transition: border-color 0.2s;
            cursor: pointer;
          }

          .filter-item input[type="text"]:focus {
            outline: none;
            border-color: #824EEB;
            box-shadow: 0 0 0 3px rgba(130, 78, 235, 0.1);
          }

          .client-dropdown-wrapper {
            position: relative;
          }

          .client-dropdown-trigger {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            background: white;
            text-align: left;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: border-color 0.2s;
          }

          .client-dropdown-trigger:hover {
            border-color: #824EEB;
          }

          .client-dropdown-trigger:focus {
            outline: none;
            border-color: #824EEB;
            box-shadow: 0 0 0 3px rgba(130, 78, 235, 0.1);
          }

          .client-dropdown-trigger.filter-active {
            border-color: #824EEB;
            box-shadow: 0 0 0 0.2rem rgba(130, 78, 235, 0.25);
          }

          .client-dropdown-menu {
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            max-height: 300px;
            overflow-y: auto;
          }

          .client-search-input {
            width: 100%;
            padding: 10px 12px;
            border: none;
            border-bottom: 1px solid #dee2e6;
            font-size: 14px;
          }

          .client-search-input:focus {
            outline: none;
          }

          .client-dropdown-item {
            padding: 10px 12px;
            cursor: pointer;
            transition: background-color 0.2s;
            border-bottom: 1px solid #f1f3f5;
          }

          .client-dropdown-item:last-child {
            border-bottom: none;
          }

          .client-dropdown-item:hover {
            background-color: #f8f9fa;
          }

          .client-dropdown-item.selected {
            background-color: #e7f1ff;
          }

          .filter-actions {
            display: flex;
            gap: 10px;
            align-items: flex-end;
          }

          .btn-apply-filter {
            padding: 8px 20px;
            background: #824EEB;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s;
            white-space: nowrap;
          }

          .btn-apply-filter:hover {
            background: #6d3ec9;
          }

          .btn-clear-filter {
            padding: 8px 20px;
            background: white;
            color: #6c757d;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            white-space: nowrap;
          }

          .btn-clear-filter:hover {
            background: #f8f9fa;
            border-color: #adb5bd;
          }

          .selected-client-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }

          .clear-client-btn {
            background: none;
            border: none;
            color: #6c757d;
            cursor: pointer;
            padding: 0;
            margin-left: 4px;
            display: inline-flex;
            align-items: center;
          }

          .clear-client-btn:hover {
            color: #dc3545;
          }

          .daterangepicker {
            z-index: 1050 !important;
          }
        `}
      </style>

      <div className="usage-filters-container">
        <div className="filter-group">
          <div className="filter-item">
            <label htmlFor="date-range">Date Range</label>
            <div
              className={`icon-form ${
                dateRange[0] || dateRange[1] ? "filter-active" : ""
              }`}
            >
              <span className="form-icon">
                <i className="ti ti-calendar" />
              </span>
              <DateRangePicker
                initialSettings={initialSettings}
                onApply={(event, picker) => {
                  onDateRangeChange(
                    picker.startDate.toDate(),
                    picker.endDate.toDate()
                  );
                }}
                onCancel={() => {
                  onDateRangeChange(null, null);
                }}
              >
                <input
                  className={`form-control ${
                    dateRange[0] || dateRange[1] ? "filter-active-input" : ""
                  }`}
                  type="text"
                  placeholder="Select Date Range"
                  readOnly
                  style={{
                    cursor: "pointer",
                    backgroundColor: "transparent",
                  }}
                />
              </DateRangePicker>
            </div>
          </div>

          <div className="filter-item" ref={dropdownRef}>
            <label htmlFor="api-client">API Client</label>
            <div className="client-dropdown-wrapper">
              <button
                type="button"
                className={`client-dropdown-trigger ${
                  selectedClientId ? "filter-active" : ""
                }`}
                onClick={() => setShowClientDropdown(!showClientDropdown)}
              >
                <span className="selected-client-badge">
                  {getSelectedClientName()}
                  {selectedClientId && (
                    <button
                      type="button"
                      className="clear-client-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearClient();
                      }}
                      title="Clear selection"
                    >
                      <i className="ti ti-x" style={{ fontSize: "16px" }}></i>
                    </button>
                  )}
                </span>
                <i
                  className={`ti ti-chevron-${
                    showClientDropdown ? "up" : "down"
                  }`}
                ></i>
              </button>

              {showClientDropdown && (
                <div className="client-dropdown-menu">
                  <input
                    type="text"
                    className="client-search-input"
                    placeholder="Search clients..."
                    value={clientSearchQuery}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    autoFocus
                  />
                  <div>
                    <div
                      className={`client-dropdown-item ${
                        selectedClientId === null ? "selected" : ""
                      }`}
                      onClick={() => {
                        onClientChange(null);
                        setShowClientDropdown(false);
                      }}
                    >
                      <strong>All Clients</strong>
                    </div>
                    {loading ? (
                      <div className="client-dropdown-item text-center">
                        <small className="text-muted">Loading...</small>
                      </div>
                    ) : apiClients.length === 0 ? (
                      <div className="client-dropdown-item text-center">
                        <small className="text-muted">No clients found</small>
                      </div>
                    ) : (
                      apiClients.map((client) => (
                        <div
                          key={client.api_client_id}
                          className={`client-dropdown-item ${
                            selectedClientId === client.api_client_id
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => handleClientSelect(client)}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {client.client_logo ? (
                              <img
                                src={client.client_logo}
                                alt={client.client_name}
                                className="avatar avatar-sm me-2 flex-shrink-0"
                              />
                            ) : (
                              <div className="avatar avatar-sm bg-light me-2 flex-shrink-0">
                                <i className="ti ti-api"></i>
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                {client.client_name}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {(dateRange[0] || dateRange[1] || selectedClientId) && (
            <div className="filter-actions">
              <button
                type="button"
                className="btn-clear-filter"
                onClick={handleClearFilters}
              >
                <i className="ti ti-x me-1"></i>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UsageFilters;
